import {
  ApplyScope,
  BlockDirection,
  BlockProtocol,
  DEFAULT_END_TASK_TIME,
  DisposeAction,
  DisposePolicySettingType,
  DisposePolicyType,
  IpStrategyField,
} from './dispose-action.constants'
import type { DisposeActionType } from './dispose-action.constants'
import {
  buildStrategyName,
  extractDomainFromThreat,
  isStatusOnlyAction,
} from './dispose-strategy-utils'
import type { DisposeFormState, DisposePayload, SecurityEventDetail } from './types'
import type { SafeEventHandlingRequestParams } from './handling-request.types'
import { toApiHandlingRange } from './handling-request.types'

const STRATEGY_GROUP_ID = -1

/** 策略应用范围：本机 = 列表 device_id；全网 = 空列表；group_id 固定 -1 */
export function buildStrategyApplyScope (
  detail: Pick<SecurityEventDetail, 'device_id'>,
  applyScope?: number,
): { device_list: string[]; group_id: number } {
  if (applyScope === ApplyScope.AllEndpoints) {
    return { device_list: [], group_id: STRATEGY_GROUP_ID }
  }
  const deviceId = detail.device_id?.trim()
  return {
    device_list: deviceId ? [deviceId] : [],
    group_id: STRATEGY_GROUP_ID,
  }
}

type IpAddressBlockingStrategy = {
  direction: number
  port_type: number
  action: number
  protocol: number
  local_port: string[]
  remote_ip: string[]
  remote_port: string[]
  frequency: number
  regular_tasks: typeof DEFAULT_END_TASK_TIME
}

/** IP 访问限制策略 strategy 字段（与 ip-address-blocking 一致） */
export function buildIpStrategyObject (
  detail: SecurityEventDetail,
  form: Pick<DisposeFormState, 'protocol' | 'direction'>,
): IpAddressBlockingStrategy {
  const remoteIp = detail.threat_entity?.trim()
  return {
    direction: Number(form.direction) || BlockDirection.Bidirectional,
    port_type: IpStrategyField.portTypeCustom,
    action: IpStrategyField.actionStopBlocking,
    protocol: Number(form.protocol) || BlockProtocol.Any,
    local_port: [''],
    remote_ip: remoteIp ? [remoteIp] : [''],
    remote_port: [''],
    frequency: IpStrategyField.frequencyDay,
    regular_tasks: [...DEFAULT_END_TASK_TIME],
  }
}

/** 域名访问限制策略 strategy 字段（与 access-restriction 一致） */
export function buildUrlStrategyObject (detail: SecurityEventDetail) {
  const domain = extractDomainFromThreat(detail.threat_entity)
  return {
    limit_type: 1,
    status: 1,
    black_list: domain,
    category_ids: [] as number[],
  }
}

/** 进程控制策略 strategy 字段（与 process-control 一致，预留） */
export function buildProcessStrategyObject (detail: SecurityEventDetail) {
  const path = detail.process_path ?? detail.threat_entity ?? ''
  const name = path.split(/[/\\]/).pop() || path
  const md5 = detail.file_md5 ?? ''
  return {
    main_switch: true,
    handle_type: 1,
    frequency: IpStrategyField.frequencyDay,
    regular_tasks: [...DEFAULT_END_TASK_TIME],
    process_infos: [
      {
        name,
        path,
        md5,
      },
    ],
  }
}

export function enrichDisposePayloadWithStrategy (
  detail: SecurityEventDetail,
  action: DisposeActionType,
  form: DisposeFormState,
  payload: DisposePayload,
): DisposePayload {
  const eventDesc = detail.event_desc || detail.threat_entity || 'event'
  const scope = buildStrategyApplyScope(detail, form.apply_scope)
  const enriched: DisposePayload = {
    ...payload,
    ...scope,
  }

  if (action === DisposeAction.BlockIp) {
    const strategyObj = buildIpStrategyObject(detail, form)
    enriched.name = buildStrategyName(eventDesc, detail.threat_entity)
    enriched.type = DisposePolicySettingType.ip_address_blocking
    enriched.strategy_type = DisposePolicyType.ip_address_blocking
    enriched.strategy = JSON.stringify(strategyObj)
    enriched.protocol = strategyObj.protocol
    enriched.direction = strategyObj.direction
    enriched.strategy_name = enriched.name
  }

  if (action === DisposeAction.BlockUrl) {
    const strategyObj = buildUrlStrategyObject(detail)
    const domain = extractDomainFromThreat(detail.threat_entity)
    enriched.name = buildStrategyName(eventDesc, domain)
    enriched.type = DisposePolicySettingType.internet
    enriched.strategy_type = DisposePolicyType.internet
    enriched.strategy = JSON.stringify(strategyObj)
    enriched.domain = domain
    enriched.limit_type = strategyObj.limit_type
    enriched.status = strategyObj.status
    enriched.black_list = strategyObj.black_list
    enriched.strategy_name = enriched.name
  }

  return enriched
}

export function buildHandlingRequest (
  detail: SecurityEventDetail,
  payload: DisposePayload,
): SafeEventHandlingRequestParams | null {
  if (!payload.business_id) return null

  const base: SafeEventHandlingRequestParams = {
    business_id: payload.business_id,
    device_list: payload.device_list,
    group_id: payload.group_id ?? STRATEGY_GROUP_ID,
  }

  switch (payload.action) {
    case DisposeAction.MarkHandled:
      return {
        ...base,
        handle_type: 2,
        range: toApiHandlingRange(payload.apply_scope),
      }
    case DisposeAction.MarkIgnored:
      return {
        ...base,
        handle_type: 3,
        range: toApiHandlingRange(payload.apply_scope),
      }
    case DisposeAction.BlockIp:
    case DisposeAction.BlockUrl:
      return {
        ...base,
        handle_type: 1,
        range: toApiHandlingRange(payload.apply_scope),
        name: payload.name ?? payload.strategy_name,
        type: payload.type,
        strategy_type: payload.strategy_type,
        strategy: payload.strategy,
      }
    default:
      if (String(payload.action).startsWith('trust_')) {
        return {
          ...base,
          handle_type: 4,
          range: toApiHandlingRange(payload.apply_scope),
        }
      }
      return null
  }
}

export function buildDisposePayload (
  detail: SecurityEventDetail,
  action: DisposeActionType,
  form: DisposeFormState,
): DisposePayload {
  const payload: DisposePayload = {
    business_id: detail.business_id,
    action,
    apply_scope: form.apply_scope,
  }

  if (isStatusOnlyAction(action)) {
    delete payload.apply_scope
  }

  if (
    action === DisposeAction.KillProcess ||
    action === DisposeAction.IsolateFile
  ) {
    delete payload.apply_scope
  }

  return enrichDisposePayloadWithStrategy(detail, action, form, payload)
}

export type { DisposeActionType } from './dispose-action.constants'
